import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  InputAdornment,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AmendPolicy,
  CancellationPolicy,
  NoShowPolicy,
} from 'api/models/Policies.type'
import SectionHeader from 'components/Common/SectionHeader'
import Policies from 'components/FormFields/PolicyFields/PolicyFields'
import React, { FC, useEffect, useState } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { updateTaxi } from 'reducers/supplierReducer'
import {
  TaxiVehicleType,
  WaitingPeriod,
  YesNo,
} from '../../../../api/models/Common/JyrneyEnums'
import { taxisAPI } from '../../../../api/vehicle/taxisAPI'
import {
  ChargeRate,
  Taxi,
  VehicleCongestionZone,
} from '../../../../api/vehicle/taxisAPI.types'
import FormInput from '../../../FormFields/FormInput/FormInput'
import FormSelect from '../../../FormFields/Select/FormSelect'
import Select from '../../../FormFields/Select/Select'
import styles from '../manageSupplierStyle'
import Notification from '../../../Notification/Notification'
import AdditionalChargesComponent from './AdditionalCharges/AdditionalChargesComponent'
import RoutePricing from './RoutePricing'
import ViewFixedRoutePricingComponent from './ViewFixedRoutePricingComponent'

export interface ChargeExceptionFormData {
  value?: string
  chargeExceptionId: string
  chargeTypeName: string
}

// export interface VehicleFormData {
//   vehicleTypeName: string
//   capacity: string
//   fleetSize: string
//   averageAge: string
//   minimumFare: string
// }
export interface WaitingPeriodFormData {
  period: string
  price: string
  chargeExceptions: Array<ChargeExceptionFormData>
}
export interface AdditionalChargeFormData {
  additionalChargeId: number
  chargeTypeName: string
  value: number
  vATTypeName: string
  applyMarkup: boolean
}
export interface VehicleFormData {
  chargeExceptions: Array<{
    chargeTypeName: string
    chargeExceptionId: string
    chargeExceptionPremiumTypeName: string
  }>
  chargeRates: Array<{
    fromMile: string
    toMile: string
    pricePerMile: string
    chargeExceptions: Array<ChargeExceptionFormData>
  }>
  vehicle: {
    vehicleTypeName: string
    isAvailableForRideHail: boolean
    capacity: string
    fleetSize: string
    averageAge: string
    minimumFare: {
      price: string
      chargeExceptions: Array<ChargeExceptionFormData>
    }
    waitingTime: {
      freePeriod: string
      gracePeriod: WaitingPeriodFormData
      chargingPeriod: WaitingPeriodFormData
    }
    additionalStop: {
      price: string
      chargeExceptions: Array<ChargeExceptionFormData>
    }
    waitAndReturn?: {
      discount: string
      chargingPeriod: WaitingPeriodFormData
    }
    cancellationPolicies?: CancellationPolicy[]
    amendPolcies?: AmendPolicy[]
    noShowPolicies?: NoShowPolicy[]
  }
  congestionZones: Array<VehicleCongestionZone>
  additionalCharges: Array<AdditionalChargeFormData>
}

const initialWatingTime = (taxi: Taxi | undefined) => {
  if (taxi) {
    if (taxi?.waitingTime?.gracePeriod?.price) {
      return 'grace'
    }
    if (taxi.waitingTime && taxi?.waitingTime?.freePeriod > 0) {
      return 'free'
    }
  }
  return ''
}

interface WaitingTimeProps {
  taxi: Taxi | undefined
  waitingTime: string
  setWaitingTime: React.Dispatch<React.SetStateAction<string>>
  isEdit: boolean
}
const WaitingTimeComponent: FC<WaitingTimeProps> = (props) => {
  const { taxi, waitingTime, setWaitingTime, isEdit } = props
  return (
    <>
      <Grid id="waiting-time" item xs={5}>
        <Select
          label="Waiting time type"
          options={WaitingPeriod}
          onChange={(e) => setWaitingTime(e.target.value)}
          value={waitingTime}
          sx={styles.inputField}
          disabled={!isEdit}
        />
      </Grid>
      {waitingTime === 'free' && (
        <>
          <Grid item xs={5}>
            <FormInput
              name="vehicle.waitingTime.freePeriod"
              label="Waiting period"
              defaultValue={taxi?.waitingTime?.freePeriod}
              disabled={!isEdit}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">minutes</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={5}>
            <FormInput
              name="vehicle.waitingTime.chargingPeriod.period"
              label="Additional waiting unit"
              defaultValue={taxi?.waitingTime?.chargingPeriod?.period}
              disabled={!isEdit}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">minutes</InputAdornment>
                ),
              }}
            />
          </Grid>
        </>
      )}
      {waitingTime === 'grace' && (
        <>
          <Grid item xs={5}>
            <FormInput
              name="vehicle.waitingTime.gracePeriod.period"
              label="Waiting period"
              defaultValue={taxi?.waitingTime?.gracePeriod?.period}
              disabled={!isEdit}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">minutes</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={5}>
            <FormInput
              name="vehicle.waitingTime.chargingPeriod.period"
              label="Additional waiting unit"
              defaultValue={taxi?.waitingTime?.chargingPeriod?.period}
              disabled={!isEdit}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">minutes</InputAdornment>
                ),
              }}
            />
          </Grid>
        </>
      )}
    </>
  )
}
interface WaitAndReturnProps {
  taxi: Taxi | undefined
  waitAndReturn: boolean
  setWaitAndReturn: React.Dispatch<React.SetStateAction<boolean>>
  isEdit: boolean
}
const WaitingAndReturnComponent: FC<WaitAndReturnProps> = (props) => {
  const { taxi, waitAndReturn, setWaitAndReturn, isEdit } = props

  return (
    <>
      <Grid item xs={5}>
        <Select
          label={`Charge for wait & return`}
          options={YesNo}
          disabled={!isEdit}
          value={waitAndReturn ? 'yes' : 'no'}
          onChange={(e) => {
            const value = e.target.value === 'yes' ? true : false
            setWaitAndReturn(value)
          }}
        />
      </Grid>
      {waitAndReturn && (
        <>
          <Grid item xs={5}>
            <FormInput
              name="vehicle.waitAndReturn.discount"
              label="Discount"
              defaultValue={taxi?.waitAndReturn?.discount}
              disabled={!isEdit}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={5}>
            <FormInput
              name="vehicle.waitAndReturn.chargingPeriod.period"
              label="Waiting period"
              defaultValue={taxi?.waitAndReturn?.chargingPeriod.period}
              disabled={!isEdit}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">minutes</InputAdornment>
                ),
              }}
            />
            <FormInput
              name="vehicle.waitAndReturn.chargingPeriod.price"
              label="Price"
              defaultValue={taxi?.waitAndReturn?.chargingPeriod.price}
              disabled={!isEdit}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">£</InputAdornment>
                ),
              }}
            />
          </Grid>
        </>
      )}
    </>
  )
}
interface TaxiDetailsProps {
  taxi: Taxi | undefined
  isEdit: boolean
}
const TaxiDetailsComponent: FC<TaxiDetailsProps> = (props) => {
  const { taxi, isEdit } = props
  const formMethods = useFormContext()
  const isAvailable = formMethods.watch(
    'vehicle.isAvailableForRideHail',
    taxi?.isAvailableForRideHail || false,
  )
  useEffect(() => {
    formMethods.register('vehicle.isAvailableForRideHail')

    formMethods.setValue(
      'vehicle.isAvailableForRideHail',
      taxi?.isAvailableForRideHail || false,
    )
  }, [])
  return (
    <Grid container gap="15px">
      <Grid item xs={5}>
        <FormSelect
          name="vehicle.vehicleTypeName"
          label="Vehicle type"
          options={TaxiVehicleType}
          disabled={!isEdit}
          defaultValue={taxi?.vehicleDetails.vehicleTypeName}
          value={taxi?.vehicleDetails.vehicleTypeName}
          onChange={(e) =>
            formMethods.setValue('vehicle.vehicleTypeName', e.target.value)
          }
        />
        <FormInput
          name="vehicle.capacity"
          label="Number of passenger"
          defaultValue={taxi?.capacity}
          disabled={!isEdit}
        />

        <FormInput
          name="vehicle.fleetSize"
          label="Number of vehicles in fleet"
          defaultValue={taxi?.fleetSize}
          disabled={!isEdit}
        />

        <FormInput
          name="vehicle.averageAge"
          label="Average year of fleet"
          defaultValue={taxi?.averageAge}
          disabled={!isEdit}
        />
      </Grid>
      <Grid item xs={5}>
        <Select
          label="Available for Ridehail"
          options={YesNo}
          disabled={!isEdit}
          value={isAvailable ? 'yes' : 'no'}
          onChange={(e) =>
            formMethods.setValue(
              'vehicle.isAvailableForRideHail',
              e.target.value === 'yes',
            )
          }
        />
      </Grid>
    </Grid>
  )
}
interface AdditionalStopsProps {
  additionalStop: boolean
  setAdditionalStop: React.Dispatch<React.SetStateAction<boolean>>
  isEdit: boolean
}
const AdditionalStops: FC<AdditionalStopsProps> = (props) => {
  const { additionalStop, setAdditionalStop, isEdit } = props
  return (
    <>
      <Grid item>
        <Typography variant={'h6'} gutterBottom>
          Additional stops
        </Typography>
      </Grid>
      <Grid item xs={5}>
        <Select
          label="Charge for additional stops"
          options={YesNo}
          disabled={!isEdit}
          value={additionalStop ? 'yes' : 'no'}
          onChange={(e) => {
            const value = e.target.value === 'yes' ? true : false
            setAdditionalStop(value)
          }}
        />
      </Grid>
    </>
  )
}
interface Props {
  taxi: Taxi | undefined
  exisitingChargeRates: Array<ChargeRate>
  backToSummaryFunction: Function
}
export const ManageTaxi: FC<Props> = (props) => {
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [notify, setNotify] = useState<{
    isOpen: boolean
    message: string
    type: 'error' | 'success' | 'info' | 'warning'
  }>({ isOpen: false, message: '', type: 'error' })
  const { taxi, exisitingChargeRates, backToSummaryFunction } = props
  const queryClient = useQueryClient()

  // get supplierId for create/update
  const supplierIdFromStore = useSelector(
    (state: any) => state.suppliers?.supplier?.id,
  )
  const resolvedSupplierId = taxi?.supplierId ?? supplierIdFromStore

  const [chargeRates, setChargeRates] =
    useState<Array<ChargeRate>>(exisitingChargeRates)
  const [waitingTime, setWaitingTime] = useState<string>(
    initialWatingTime(taxi),
  )
  const [additionalStop, setAdditionalStop] = useState<boolean>(
    taxi?.additionalStop?.price ? true : false,
  )
  const [waitAndReturn, setWaitAndReturn] = useState<boolean>(
    taxi?.waitAndReturn?.discount ? true : false,
  )

  const defaultValues = () => {
    if (taxi && exisitingChargeRates) {
      return {
        vehicle: taxi,
        chargeRates: exisitingChargeRates,
        congestionZones: taxi.congestionZones,
        additionalCharges: taxi.additionalCharges,
      }
    }
    return {}
  }
  const formMethods = useForm({
    defaultValues: defaultValues(),
  })

  useEffect(() => {
    setChargeRates(exisitingChargeRates)
  }, [exisitingChargeRates])

  useEffect(() => {
    if (!taxi?.id) setIsEdit(true)
  }, [taxi])

  const mutation = useMutation((payload: VehicleFormData) => {
    console.log('payload', payload)
    if (!resolvedSupplierId) {
      throw new Error('supplierId is required')
    }
    return taxi?.id
      ? taxisAPI.updateTaxi(taxi.id, payload.vehicle)
      : taxisAPI.createTaxi(resolvedSupplierId, payload)
  })

  const showApiError = (err: any, fallback: string) => {
    const validationErrors: Array<{ message: string; path?: string }> =
      err?.response?.data?.error?.validationErrors ?? []
    const message =
      validationErrors.length > 0
        ? validationErrors.map((v) => v.message).join(' ')
        : err?.response?.data?.error?.message ?? err?.message ?? fallback
    setNotify({ isOpen: true, message, type: 'error' })
  }

  const buildChargeRatePayload = (raw: any) => {
    const chargeExceptions = Array.isArray(raw?.chargeExceptions)
      ? raw.chargeExceptions
          .filter(
            (ce: any) =>
              ce &&
              ce.chargeExceptionId &&
              ce.chargeTypeName &&
              ce.value !== undefined &&
              ce.value !== '',
          )
          .map((ce: any) => ({
            chargeExceptionId: Number(ce.chargeExceptionId),
            chargeTypeName: ce.chargeTypeName,
            value: Number(ce.value),
          }))
      : []
    const toMile = Number(raw?.toMile)
    return {
      fromMile: Number(raw?.fromMile) || 0,
      ...(toMile > 0 ? { toMile } : {}),
      pricePerMile: Number(raw?.pricePerMile) || 0,
      chargeExceptions,
    }
  }

  const persistChargeRates = async (data: any, taxiId: number) => {
    const formRates: any[] = data?.chargeRates || []
    if (formRates.length === 0 && exisitingChargeRates.length > 0) {
      return
    }
    if (formRates.length < chargeRates.length) {
      return
    }
    const submitted = formRates.map(buildChargeRatePayload)

    const updates: Array<Promise<any>> = []
    const deletesForRecreate: number[] = []
    const creates: any[] = []
    submitted.forEach((rate, i) => {
      const existing = exisitingChargeRates[i]
      if (!existing?.id) {
        creates.push(rate)
        return
      }
      const existingFrom = Number(existing.fromMile) || 0
      const existingTo = Number(existing.toMile) || 0
      const mileageChanged =
        existingFrom !== rate.fromMile || existingTo !== (rate.toMile ?? 0)
      if (mileageChanged) {
        deletesForRecreate.push(existing.id)
        creates.push(rate)
      } else {
        updates.push(taxisAPI.updateChargeRate(taxiId, existing.id, rate))
      }
    })

    await Promise.all(updates)
    await Promise.all(
      deletesForRecreate.map((id) => taxisAPI.deleteChargeRate(taxiId, id)),
    )
    if (creates.length > 0) {
      await taxisAPI.createChargeRate(taxiId, creates)
    }
  }

  const patchDirtyFixedRoutes = async (data: any) => {
    const dirtyRoutes = formMethods.formState.dirtyFields?.fixedRoutes
    const routes = data.fixedRoutes
    if (!taxi?.id || !dirtyRoutes || !routes) return
    const indices = Object.keys(dirtyRoutes)
    await Promise.all(
      indices.map((indexKey) => {
        const i = Number(indexKey)
        const route = routes[i]
        if (!route) return Promise.resolve()
        return taxisAPI.updateFixedRoute(taxi.id!, route.fixedRouteId, {
          ...route,
          price: Number(route.price),
        })
      }),
    )
  }

  const onSubmit = async (data: VehicleFormData) => {
    console.log('>>>>', data)
    if (taxi && data.vehicle?.waitingTime) {
      const mergedWaitingTime: any = {
        ...taxi.waitingTime,
        ...data.vehicle.waitingTime,
      }
      const mergedGracePeriod: any = {
        ...taxi.waitingTime?.gracePeriod,
        ...data.vehicle.waitingTime?.gracePeriod,
      }
      if (mergedGracePeriod.period && mergedGracePeriod.price) {
        mergedWaitingTime.gracePeriod = mergedGracePeriod
      } else {
        delete mergedWaitingTime.gracePeriod
      }
      mergedWaitingTime.chargingPeriod = {
        ...taxi.waitingTime?.chargingPeriod,
        ...data.vehicle.waitingTime?.chargingPeriod,
      }
      data.vehicle.waitingTime = mergedWaitingTime
    }
    if (!waitAndReturn) delete data.vehicle.waitAndReturn

    const dirtyFields = formMethods.formState.dirtyFields as any
    const hasNestedDirty = (obj: any): boolean => {
      if (!obj) return false
      if (typeof obj !== 'object') return Boolean(obj)
      return Object.values(obj).some((v) => hasNestedDirty(v))
    }
    const vehicleDirty = hasNestedDirty(dirtyFields?.vehicle)

    try {
      if (vehicleDirty || !taxi?.id) {
        const response = await mutation.mutateAsync(data)
        if (
          response.status !== 200 &&
          response.status !== 201 &&
          response.status !== 204
        ) {
          return
        }
      }
      if (taxi?.id) {
        const chargeRatesDirty = hasNestedDirty(dirtyFields?.chargeRates)
        if (chargeRatesDirty) {
          try {
            await persistChargeRates(data, taxi.id)
            await queryClient.invalidateQueries({
              queryKey: ['getChargeRates', taxi.id],
            })
          } catch (err) {
            showApiError(err, 'Failed to update charge rates.')
            return
          }
        }
      }
      try {
        await patchDirtyFixedRoutes(data)
      } catch (err) {
        showApiError(err, 'Failed to update fixed route pricing.')
        return
      }
      backToSummaryFunction()
    } catch (err) {
      console.log('>>>>', data, err)
      showApiError(err, 'Failed to save vehicle.')
    }
  }

  const onError = (errors: any, e: any) => console.log(errors, e)

  formMethods.register('vehicle.vehicleTypeName')

  return (
    <Grid container spacing={4} direction="column" sx={{ marginTop: '10px' }}>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit, onError)}>
          <Grid item xs={12} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Accordion expanded>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls=""
                id=""
              >
                <Typography component="span">
                  <SectionHeader
                    title="Vehicle Details"
                    subtitle={
                      'Size, shape, age of the average card of this type across your fleet'
                    }
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TaxiDetailsComponent taxi={taxi} isEdit={isEdit} />
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls=""
                id=""
              >
                <Typography component="span">
                  <SectionHeader
                    title="Pickup Waiting Charges"
                    subtitle={
                      'Define how long a passenger can wait for free before charges apply and the time increments used to calculate additional waiting fees.'
                    }
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <WaitingTimeComponent
                  taxi={taxi}
                  waitingTime={waitingTime}
                  setWaitingTime={setWaitingTime}
                  isEdit={isEdit}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls=""
                id=""
              >
                <Typography component="span">
                  <SectionHeader
                    title="Wait & Return Charges"
                    subtitle={
                      'Specify whether wait-and-return trips are charged, how much discount applies, and the length of time included in the waiting period.'
                    }
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {' '}
                <WaitingAndReturnComponent
                  taxi={taxi}
                  waitAndReturn={waitAndReturn}
                  setWaitAndReturn={setWaitAndReturn}
                  isEdit={isEdit}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            {/* <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls=""
                id=""
              >
                <Typography component="span">
                  <SectionHeader
                    title="Congestion Zone Charges"
                    subtitle={
                      'Set up your additional charges if you go through specific zones.'
                    }
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <CongestionZoneComponent taxiId={taxi?.id} isEdit={isEdit} />
              </AccordionDetails>
            </Accordion> */}
          </Grid>
          <Grid item xs={12} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls=""
                id=""
              >
                <Typography component="span">
                  <SectionHeader
                    title="Charge rates"
                    subtitle={
                      'Define the pricing rules for a route, including mileage ranges, minimum fare, waiting time charges, special rates for day hours or holidays, and whether additional stops incur extra fees.'
                    }
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <RoutePricing
                  taxi={taxi}
                  waitingTime={waitingTime}
                  chargeRates={chargeRates}
                  additionalStop={additionalStop}
                  waitAndReturn={waitAndReturn}
                  setChargeRates={setChargeRates}
                  isEdit={isEdit}
                />
                <AdditionalStops
                  additionalStop={additionalStop}
                  setAdditionalStop={setAdditionalStop}
                  isEdit={isEdit}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls=""
                id=""
              >
                <Typography component="span">
                  <SectionHeader
                    title="Additional Charges"
                    subtitle={'Set out additional charges'}
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <AdditionalChargesComponent taxiId={taxi?.id} isEdit={isEdit} />
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls=""
                id=""
              >
                <Typography component="span">
                  <SectionHeader
                    title="Fixed Route Pricing"
                    subtitle={'Set pricing for fixed routes'}
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ViewFixedRoutePricingComponent
                  taxiId={taxi?.id}
                  isEdit={isEdit}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="policy-manager"
                id="policy-manager"
              >
                <Typography component="span">
                  <SectionHeader
                    title="Policies"
                    subtitle={
                      'Set the policies appliable to the journeys within the platform'
                    }
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Policies
                  policies={{
                    cancellationPolicies: taxi?.cancellationPolicies ?? [],
                    noShowPolicies: taxi?.noShowPolicies ?? [],
                    amendPolicies: taxi?.amendPolicies ?? [],
                  }}
                  updateFn={updateTaxi}
                  updateSchema={{ taxiId: taxi?.id }}
                  editPolicy={isEdit}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid
            container
            justifyContent="flex-end"
            direction="row"
            alignItems="center"
          >
            <Grid
              container
              item
              justifyContent="flex-end"
              xs={5}
              direction="row"
            >
              <Grid item xs={2}>
                <Button
                  type="button" // Add this
                  variant="outlined"
                  className={`${styles.ctaSpacing} ${styles.round} ${styles['jyrneyBlue']}`}
                  onClick={() => backToSummaryFunction()}
                >
                  BACK
                </Button>
              </Grid>
              <Grid item xs={2}>
                {isEdit ? (
                  <Button
                    type="button" // change from "submit"
                    onClick={formMethods.handleSubmit(onSubmit, onError)} // submit explicitly
                    variant="outlined"
                    className={`${styles.ctaSpacing} ${styles.round} ${styles['jyrneyYellow']}`}
                  >
                    SAVE
                  </Button>
                ) : taxi?.id !== undefined ? (
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault() // prevent the click from turning into a submit
                      setIsEdit(true)
                    }}
                    variant="outlined"
                    className={`${styles.ctaSpacing} ${styles.round} ${styles['jyrneyYellow']}`}
                  >
                    EDIT
                  </Button>
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
          </Grid>
          {/* <Grid item xs={12} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls=""
                id=""
              >
                <Typography component="span">
                  <SectionHeader title="Title" subtitle={'Subtitle'} />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>Put your content here</AccordionDetails>
            </Accordion>
          </Grid> */}
        </form>
      </FormProvider>
      <Notification notify={notify} setNotify={setNotify} />
    </Grid>
  )
}
export default ManageTaxi
